/**
 * Content model for the EHR trial dashboard (TTFV Story 3) — the primary home
 * for trial users who answered "I'm exploring a new EHR" in the trial-intent
 * modal. It lives in its own file rather than inside TrialHome.type.ts because
 * it is a whole page's worth of copy; `TrialHomeContent.ehrDashboard` is the
 * single key that hangs it off the stored `trialHome` AppData row.
 *
 * Every string here is admin-editable in chiroup-admin's Trial Experience
 * editor. Video fields take pasted share links (YouTube / Loom / Vimeo) which
 * `toAutoplayEmbedUrl` normalizes at render time; a blank URL hides whatever
 * control would have opened it, so the page never shows an empty player.
 *
 * SAOP is deliberately spelled S-A-O-P throughout. It is not a typo for SOAP.
 */

/**
 * The steps of the "Learn & Demo the ChiroUp EHR" roadmap, in render order.
 *
 * `follow-up` is the 2b sub-step of SAOP, not an independent EHR module. Its
 * node renders in a coming-soon state and stays out of the explored count
 * until a video or destination URL is configured. Keeping it in this list
 * means the ids the analytics events carry never have to change.
 */
export const EHR_EXPLORE_STEP_IDS = [
  'schedule',
  'saop-initial',
  'follow-up',
  'billing',
  'reporting',
] as const;

export type EhrExploreStepId = (typeof EHR_EXPLORE_STEP_IDS)[number];

/**
 * Which roadmap steps this user has explored. Rebuilt from their
 * `TrialEhrStepExplored` events rather than stored as its own column, so
 * progress survives sessions and devices for the life of the trial without a
 * new table (same approach as the trial checklist).
 */
export type EhrExploreProgress = {
  exploredSteps: EhrExploreStepId[];
};

/**
 * Narrow raw `metadata.stepId` values from the event log to known step ids,
 * deduped and in roadmap order.
 *
 * The log is append-only and outlives the code that wrote it: a step id that
 * was renamed or dropped in a later release still has rows. Passing those
 * through would inflate the explored count past the number of steps on screen.
 */
export const parseExploredSteps = (raw: readonly unknown[]): EhrExploreStepId[] => {
  const seen = new Set(raw.filter((v): v is string => typeof v === 'string'));

  return EHR_EXPLORE_STEP_IDS.filter(id => seen.has(id));
};

/**
 * Which video a video-tracking event refers to. Short enumerated label, safe
 * for `ClinicEvent.metadata` (see trackEventMetadataKeys).
 */
export const EHR_VIDEO_KEYS = [
  'saop-methodology',
  'schedule',
  'saop-initial',
  'follow-up',
  'billing',
  'reporting',
  'testimonial',
] as const;

export type EhrVideoKey = (typeof EHR_VIDEO_KEYS)[number];

/** One expandable switching/onboarding FAQ row. */
export type EhrDashboardFaq = {
  question: string;
  /** Left blank in the defaults: the spec supplies the three questions but no
   *  answers. A row with no answer still expands, showing the admin prompt
   *  instead of an empty panel. */
  answer: string;
};

/** One row in the "Explore reports" preview modal. */
export type EhrReportingItem = {
  title: string;
  description: string;
};

export type EhrTrialDashboardContent = {
  /* Section 1 — The ChiroUp difference: SAOP */
  saopHeading: string;
  saopSubhead: string;
  saopDescription: string;
  saopPrimaryCta: string;
  saopSecondaryCta: string;
  /** "Why SAOP?" methodology video. Blank hides the secondary CTA entirely. */
  saopVideoUrl: string;

  /* Section 2 — Learn & Demo the ChiroUp EHR */
  learnHeading: string;
  learnSupportingCopy: string;
  /** Roadmap node labels. Editable, but they name product areas rather than
   *  describing them, so there is rarely a reason to change them. */
  scheduleTitle: string;
  saopInitialTitle: string;
  followUpTitle: string;
  billingTitle: string;
  reportingTitle: string;
  scheduleCta: string;
  scheduleVideoUrl: string;
  saopInitialCta: string;
  followUpSupportingCopy: string;
  followUpCta: string;
  billingCta: string;
  billingVideoUrl: string;
  reportingCta: string;
  reportingModalTitle: string;
  reportingItems: EhrReportingItem[];
  /**
   * Per-step action URLs (Kara, 2026-10-06). Each roadmap step is configured
   * with EITHER a video URL (CTA opens the clip in a modal) OR a URL — a
   * leading-"/" in-app path navigates within ChiroUp, anything else opens in
   * a new browser tab. The two are mutually exclusive per step (admin UX and
   * the write-time schema both enforce it); with neither set the step renders
   * as "Coming soon". Resolution lives in {@link resolveEhrStepAction}.
   */
  scheduleUrl: string;
  saopInitialVideoUrl: string;
  saopInitialUrl: string;
  followUpVideoUrl: string;
  followUpUrl: string;
  billingUrl: string;
  reportingVideoUrl: string;
  reportingUrl: string;
  speedCalloutHeading: string;
  speedCalloutCopy: string;

  /* Section 3 — Now see ChiroUp EHR in action */
  walkthroughHeading: string;
  walkthroughSupportingCopy: string;
  walkthroughCta: string;
  walkthroughSocialProof: string;
  /** Faces beside the social-proof line. Rendered as an overlapping cluster;
   *  with none set the line stands on its own rather than showing placeholders. */
  walkthroughAvatars: string[];

  /* Section 4 — Questions before your walkthrough? */
  switchingHeading: string;
  switchingSupportingCopy: string;
  testimonialHeadline: string;
  testimonialAuthor: string;
  testimonialCta: string;
  /** The line under the attribution, e.g. what their switch actually looked
   *  like. Separate from the headline so the quote can stay short. */
  testimonialSupportingCopy: string;
  testimonialVideoUrl: string;
  /** Poster frame for the clip. Uploaded to S3 by the admin editor: embed
   *  providers don't expose a thumbnail this page is allowed to fetch. */
  testimonialThumbnail: string;
  /** Runtime badge on the thumbnail, e.g. "1:22". Typed in, since we can't
   *  read it from the embed. */
  testimonialDuration: string;
  faqs: EhrDashboardFaq[];
  reassuranceHeading: string;
  reassuranceCopy: string;
  reassuranceProofPoints: string[];

  /* Section 5 — Explore our clinical tools */
  essentialsEyebrow: string;
  essentialsHeading: string;
  essentialsSupportingCopy: string;
  essentialsCta: string;
  essentialsCategories: string[];

  /**
   * How long a video modal must stay open before the step counts as explored.
   * The embeds are third-party iframes with no playback telemetry available to
   * this page, so dwell time is the proxy for the spec's watch threshold.
   * Admin-configurable because the right number depends on clip length.
   */
  videoWatchThresholdSeconds: number;
};

export const DEFAULT_EHR_TRIAL_DASHBOARD: EhrTrialDashboardContent = {
  saopHeading: 'The ChiroUp difference: SAOP',
  saopSubhead: "(That's not a typo.)",
  saopDescription: 'RSAOP flips the script so you can document faster.',
  saopPrimaryCta: 'Try the SAOP Note',
  saopSecondaryCta: 'Why SAOP?',
  saopVideoUrl: '',

  learnHeading: 'Learn & Demo the ChiroUp EHR',
  learnSupportingCopy:
    'See how every step works, then experience how fast follow-ups come together.',
  scheduleTitle: 'Schedule',
  saopInitialTitle: 'SAOP (Initial Note)',
  followUpTitle: 'Follow-Up Note',
  billingTitle: 'Billing',
  reportingTitle: 'Reporting',
  scheduleCta: 'Watch how it works',
  scheduleVideoUrl: '',
  saopInitialCta: 'Try it now',
  followUpSupportingCopy: 'See how fast follow-ups are.',
  followUpCta: 'Try follow-up',
  billingCta: 'Watch how it works',
  billingVideoUrl: '',
  reportingCta: 'Explore reports',
  reportingModalTitle: 'Reporting in ChiroUp',
  reportingItems: [],
  scheduleUrl: '',
  saopInitialVideoUrl: '',
  // The SAOP step launches the sandbox out of the box — repointable in admin.
  saopInitialUrl: '/soap-sandbox',
  followUpVideoUrl: '',
  followUpUrl: '',
  billingUrl: '',
  reportingVideoUrl: '',
  reportingUrl: '',
  speedCalloutHeading: 'Experience the speed of SAOP',
  speedCalloutCopy:
    "Initial note to follow-up in minutes, not hours. That's the ChiroUp difference.",

  walkthroughHeading: 'Now see ChiroUp EHR in action',
  walkthroughSupportingCopy: 'See how this workflow comes together for your practice.',
  walkthroughCta: 'Schedule my personalized walkthrough',
  walkthroughSocialProof: 'Join 1000+ practices already growing with ChiroUp',
  walkthroughAvatars: [],

  switchingHeading: 'Questions before your walkthrough?',
  switchingSupportingCopy: "We've got answers, and switching to ChiroUp is easier than you think.",
  testimonialHeadline: 'Switching was easier than I expected.',
  testimonialAuthor: '',
  testimonialCta: 'Watch how it went',
  testimonialSupportingCopy: '',
  testimonialVideoUrl: '',
  testimonialThumbnail: '',
  testimonialDuration: '',
  faqs: [
    { question: 'How does migration work?', answer: '' },
    { question: 'What does onboarding look like?', answer: '' },
    { question: 'How do payments and billing work?', answer: '' },
  ],
  reassuranceHeading: "You're not alone in this.",
  reassuranceCopy:
    'Our onboarding team handles the heavy lifting so you can focus on your patients, not the process.',
  reassuranceProofPoints: [
    'Dedicated Onboarding Coach',
    'Average go-live in 3-4 weeks',
    'Live support every step of the way',
  ],

  essentialsEyebrow: 'INCLUDED WITH YOUR TRIAL',
  essentialsHeading: 'Explore our clinical tools',
  essentialsSupportingCopy:
    'Patient education, condition reports, and resources trusted by thousands of practices.',
  essentialsCta: 'View Essentials tools',
  essentialsCategories: [
    'Patient education',
    'Condition reports',
    'Clinical resources',
    'Practice growth',
  ],

  videoWatchThresholdSeconds: 15,
};

/**
 * Hydrate a stored `ehrDashboard` block. Mirrors
 * `mergeTrialHomeWithDefaults`: a missing field falls back to the default
 * rather than landing as `undefined`, and stored arrays replace the default
 * wholesale (an admin who removed an FAQ row means it to stay removed, so
 * element-wise merging would resurrect it).
 *
 * A stored-but-empty array is the one case we treat as "not set": the arrays
 * ship either empty by design (reportingItems) or fully populated, and an
 * editor that round-trips `[]` for an untouched field shouldn't silently wipe
 * copy the admin never saw.
 */
export function mergeEhrDashboardWithDefaults(
  stored: Partial<EhrTrialDashboardContent> | null | undefined
): EhrTrialDashboardContent {
  if (!stored) return DEFAULT_EHR_TRIAL_DASHBOARD;

  // List fields fall back to the defaults only when the KEY is absent (a row
  // stored before the field existed). A stored array is respected verbatim —
  // including an empty one, which is an admin deliberately deleting every row
  // and must stay deleted (Kara, 2026-09-28: removed "Explore our clinical
  // tools" categories kept resurrecting). The editor loads merged content, so
  // an untouched list round-trips the default VALUES, never [].
  return {
    ...DEFAULT_EHR_TRIAL_DASHBOARD,
    ...stored,
    faqs: stored.faqs ?? DEFAULT_EHR_TRIAL_DASHBOARD.faqs,
    reportingItems: stored.reportingItems ?? DEFAULT_EHR_TRIAL_DASHBOARD.reportingItems,
    walkthroughAvatars: stored.walkthroughAvatars ?? DEFAULT_EHR_TRIAL_DASHBOARD.walkthroughAvatars,
    reassuranceProofPoints:
      stored.reassuranceProofPoints ?? DEFAULT_EHR_TRIAL_DASHBOARD.reassuranceProofPoints,
    essentialsCategories:
      stored.essentialsCategories ?? DEFAULT_EHR_TRIAL_DASHBOARD.essentialsCategories,
  };
}

/** What a roadmap step's CTA does, resolved from the admin config. */
export type EhrStepAction =
  | { kind: 'video'; url: string; videoKey: EhrVideoKey }
  | { kind: 'route'; path: string }
  | { kind: 'external'; url: string }
  /** Reporting's items modal — kept as that step's fallback so existing
   *  configured content doesn't regress to Coming Soon. */
  | { kind: 'reporting' }
  | { kind: 'coming-soon' };

const STEP_VIDEO_KEYS: Record<EhrExploreStepId, EhrVideoKey> = {
  schedule: 'schedule',
  'saop-initial': 'saop-initial',
  'follow-up': 'follow-up',
  billing: 'billing',
  reporting: 'reporting',
};

/**
 * Per-step CTA resolution (Kara, 2026-10-06): video URL → clip in a modal;
 * URL with a leading "/" → in-app navigation; any other URL → new browser
 * tab; neither → Coming Soon. Video wins over URL as a belt-and-braces rule —
 * the admin UX and write-time schema keep the two mutually exclusive, but a
 * stored row that slipped past both must not dead-end the step. Shared by the
 * EHR dashboard, the unsure dashboard's workflow row, and the admin preview
 * so the three can never disagree.
 */
export const resolveEhrStepAction = (
  content: EhrTrialDashboardContent,
  stepId: EhrExploreStepId
): EhrStepAction => {
  const fields: Record<EhrExploreStepId, { video: string; url: string }> = {
    schedule: { video: content.scheduleVideoUrl, url: content.scheduleUrl },
    'saop-initial': { video: content.saopInitialVideoUrl, url: content.saopInitialUrl },
    'follow-up': { video: content.followUpVideoUrl, url: content.followUpUrl },
    billing: { video: content.billingVideoUrl, url: content.billingUrl },
    reporting: { video: content.reportingVideoUrl, url: content.reportingUrl },
  };

  const video = fields[stepId].video?.trim();
  const url = fields[stepId].url?.trim();

  if (video) return { kind: 'video', url: video, videoKey: STEP_VIDEO_KEYS[stepId] };
  if (url) {
    return url.startsWith('/') ? { kind: 'route', path: url } : { kind: 'external', url };
  }
  if (stepId === 'reporting' && content.reportingItems.length > 0) return { kind: 'reporting' };

  return { kind: 'coming-soon' };
};
